import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSite, isValidSiteSha, getSiteSaveReadiness } from "./site";
import { adminUpdateSite } from "./api";
import { formatApiError } from "./auth";

const InlineEditContext = createContext({ openEditor: () => {} });

export function useInlineEdit() {
  return useContext(InlineEditContext);
}

// One shared editor dialog for every editable text block on the public pages.
// Saving writes the whole settings document (structured fields untouched,
// only the texts map gains/overrides one key).
export function InlineEditProvider({ children }) {
  const { site, loading, loadError, reload } = useSite();
  const [edit, setEdit] = useState(null); // { key, value, multiline, defaultValue, baseSha, dirty }
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(null); // { serverValue, serverSite }
  const [saving, setSaving] = useState(false);

  const openEditor = (key, value, multiline, defaultValue = value) => {
    setError("");
    setConflict(null);
    setEdit({
      key,
      value,
      multiline,
      defaultValue,
      baseSha: isValidSiteSha(site?.content_sha) ? site.content_sha : null,
      dirty: false,
    });
  };

  useEffect(() => {
    if (edit && !edit.baseSha && isValidSiteSha(site?.content_sha)) {
      const loadedValue =
        site.texts && edit.key in site.texts ? site.texts[edit.key] : edit.defaultValue;
      setEdit((prev) =>
        prev && !prev.baseSha
          ? {
              ...prev,
              value: prev.dirty ? prev.value : loadedValue,
              baseSha: site.content_sha,
            }
          : prev
      );
    }
  }, [edit, site]);

  const handleConflict = async (currentEdit) => {
    const latestSite = await reload();
    if (latestSite && isValidSiteSha(latestSite.content_sha)) {
      const serverValue =
        latestSite.texts && currentEdit.key in latestSite.texts
          ? latestSite.texts[currentEdit.key]
          : currentEdit.defaultValue ?? "";
      setConflict({ serverValue, serverSite: latestSite });
      setError(
        "Conflict detected: Site settings were updated in another tab or session. Both the saved server version and your unsaved draft are preserved below."
      );
    } else {
      setError("Conflict detected: Site settings changed since they were loaded. Please reload and try again.");
    }
  };

  const save = async () => {
    if (!edit) return;
    const targetSite = conflict?.serverSite || site;
    const expectedSha = conflict?.serverSite
      ? conflict.serverSite.content_sha
      : (edit.baseSha ?? site?.content_sha);
    const check = getSiteSaveReadiness({
      site: targetSite,
      loading,
      loadError,
      expectedSha,
    });
    if (!check.ready) {
      setError(check.message);
      return;
    }
    setSaving(true);
    setError("");
    try {
      await adminUpdateSite({
        bio: targetSite.bio,
        toolset: targetSite.toolset,
        goal_text: targetSite.goal_text,
        github_url: targetSite.github_url,
        studying: targetSite.studying,
        horizon: targetSite.horizon,
        goals: targetSite.goals,
        texts: { ...(targetSite.texts || {}), [edit.key]: edit.value },
        content_sha: expectedSha,
      });
      await reload();
      setConflict(null);
      setEdit(null);
      toast.success("Text updated");
    } catch (err) {
      if (err?.response?.status === 409) {
        await handleConflict(edit);
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const revert = async () => {
    if (!edit) return;
    const targetSite = conflict?.serverSite || site;
    const expectedSha = conflict?.serverSite
      ? conflict.serverSite.content_sha
      : (edit.baseSha ?? site?.content_sha);
    const check = getSiteSaveReadiness({
      site: targetSite,
      loading,
      loadError,
      expectedSha,
    });
    if (!check.ready) {
      setError(check.message);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const texts = { ...(targetSite.texts || {}) };
      delete texts[edit.key];
      await adminUpdateSite({
        bio: targetSite.bio,
        toolset: targetSite.toolset,
        goal_text: targetSite.goal_text,
        github_url: targetSite.github_url,
        studying: targetSite.studying,
        horizon: targetSite.horizon,
        goals: targetSite.goals,
        texts,
        content_sha: expectedSha,
      });
      await reload();
      setConflict(null);
      setEdit(null);
      toast.success("Back to the original text");
    } catch (err) {
      if (err?.response?.status === 409) {
        await handleConflict(edit);
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const useServerVersion = () => {
    if (!edit || !conflict) return;
    setEdit({
      ...edit,
      value: conflict.serverValue,
      baseSha: conflict.serverSite?.content_sha,
      dirty: false,
    });
    setConflict(null);
    setError("");
  };

  const expectedSha = conflict?.serverSite
    ? conflict.serverSite.content_sha
    : (edit?.baseSha ?? site?.content_sha);
  const saveReadiness = getSiteSaveReadiness({
    site: conflict?.serverSite || site,
    loading,
    loadError,
    expectedSha,
  });
  const canSave = saveReadiness.ready && !saving;

  const activeTexts = (conflict?.serverSite || site)?.texts;
  const isCustomized = edit && activeTexts && edit.key in activeTexts;

  return (
    <InlineEditContext.Provider value={{ openEditor }}>
      {children}
      <Dialog open={!!edit} onOpenChange={(open) => { if (!open) { setEdit(null); setConflict(null); setError(""); } }}>
        <DialogContent className="bg-panel border-white/10 text-mist max-w-lg" data-testid="inline-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white uppercase">Edit Text</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-4">
              <p className="font-mono text-[10px] text-dim">key: {edit.key}</p>
              {loading && !saving && (
                <div
                  className="p-3 rounded bg-white/5 border border-white/10 font-mono text-xs text-dim"
                  data-testid="inline-edit-loading"
                >
                  Loading latest site version from server... Editing is disabled until initialization completes.
                </div>
              )}
              {!loading && !saveReadiness.ready && (
                <div
                  className="p-3 rounded bg-red-500/10 border border-red-500/40 font-mono text-xs text-red-400 space-y-2"
                  data-testid="inline-edit-load-error"
                >
                  <p>{saveReadiness.message}</p>
                  <button
                    type="button"
                    onClick={() => reload()}
                    className="px-2.5 py-1 rounded border border-red-400/50 text-red-300 text-[10px] uppercase hover:bg-red-500/20 transition-colors"
                    data-testid="inline-edit-retry-load"
                  >
                    Retry Load
                  </button>
                </div>
              )}
              {error && (
                <div className="p-3 rounded bg-red-500/10 border border-red-500/40 font-mono text-xs text-red-400" data-testid="inline-edit-error">
                  {error}
                </div>
              )}
              {conflict && (
                <div className="p-3 rounded bg-amber-500/10 border border-amber-500/40 space-y-2" data-testid="inline-edit-conflict">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] uppercase text-amber-400 font-bold">Saved Server Version</span>
                    <button
                      type="button"
                      onClick={useServerVersion}
                      className="px-2 py-1 rounded border border-amber-400/50 text-amber-300 font-mono text-[10px] hover:bg-amber-500/20 transition-colors"
                      data-testid="inline-edit-use-server"
                    >
                      Use Server Version
                    </button>
                  </div>
                  <div
                    className="p-2 rounded bg-deep border border-white/10 font-mono text-xs text-mist whitespace-pre-wrap break-words"
                    data-testid="inline-edit-conflict-server-value"
                  >
                    {conflict.serverValue}
                  </div>
                </div>
              )}
              {edit.multiline ? (
                <textarea rows={5} value={edit.value} maxLength={2000} disabled={!canSave}
                  onChange={(e) => setEdit({ ...edit, value: e.target.value, dirty: true })}
                  className="w-full px-3 py-2 rounded bg-deep border border-white/10 text-white text-sm focus:outline-none focus:border-cyber disabled:opacity-50"
                  data-testid="inline-edit-textarea" />
              ) : (
                <input type="text" value={edit.value} maxLength={300} disabled={!canSave}
                  onChange={(e) => setEdit({ ...edit, value: e.target.value, dirty: true })}
                  className="w-full px-3 py-2 rounded bg-deep border border-white/10 text-white text-sm focus:outline-none focus:border-cyber disabled:opacity-50"
                  data-testid="inline-edit-input" />
              )}
              <div className="flex items-center justify-between gap-3">
                {isCustomized ? (
                  <button type="button" onClick={revert} disabled={!canSave}
                    className="px-3 py-2 rounded border border-white/20 text-dim font-mono text-[11px] hover:text-amber-400 hover:border-amber-400 transition-colors disabled:opacity-50"
                    data-testid="inline-edit-revert">
                    Revert to original
                  </button>
                ) : <span />}
                <button type="button" onClick={save} disabled={!canSave || !edit.value.trim()}
                  className="px-5 py-2 rounded bg-cyber text-[#0b0f19] font-bold font-mono text-xs hover:bg-white transition-colors disabled:opacity-50"
                  data-testid="inline-edit-save">
                  {saving
                    ? "Saving..."
                    : loading
                      ? "Loading..."
                      : !saveReadiness.ready
                        ? "Save Unavailable"
                        : conflict
                          ? "Save My Draft on Latest"
                          : "Save"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </InlineEditContext.Provider>
  );
}
